import { useMemo, useState } from 'react';
import {
  Button,
  Group,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconDownload } from '@tabler/icons-react';
import {
  ANALYTICS_EVENT_LABELS,
  ANALYTICS_EVENT_TYPES,
  DEMO_FEEDBACK_QUESTIONS,
  DEMO_SCENARIO_LABELS,
  analyticsDetailLabel,
  type AnalyticsEventType,
  type AnalyticsFunnel,
  type AnalyticsScenarioStats,
  type AnalyticsSummary,
  type DemoFeedbackQuestion,
} from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import {
  ADMIN_ANALYTICS_KEY,
  analyticsExportUrl,
  fetchAnalyticsEvents,
  fetchAnalyticsSummary,
  type AnalyticsFilter,
} from './analytics-api';

/**
 * Аналитика знакомства с игрой (концепция v0.3, п. 4; docs/ANALYTICS.md).
 *
 * Простой раздел: как люди доходят от регистрации до демо, итога и покупки,
 * чем отличаются «для себя» и «как ведущий», и список событий с выгрузкой в
 * таблицу. Ответов на карты и личных запросов здесь нет — их не собирают.
 */

const DAY = 24 * 60 * 60 * 1000;
const PERIODS = [
  { value: '7', label: '7 дней' },
  { value: '30', label: '30 дней' },
  { value: '90', label: '90 дней' },
  { value: 'all', label: 'Всё время' },
];

const formatDate = (iso: string, withTime = true): string =>
  new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });

const share = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

export function AdminAnalyticsPage() {
  const [period, setPeriod] = useState('30');
  /* Начало периода считается при выборе, а не на каждой отрисовке: иначе запрос менялся бы постоянно. */
  const from = useMemo(
    () => (period === 'all' ? undefined : new Date(Date.now() - Number(period) * DAY).toISOString()),
    [period],
  );
  const summary = useQuery({
    queryKey: [...ADMIN_ANALYTICS_KEY, 'summary', from ?? 'all'],
    queryFn: () => fetchAnalyticsSummary({ from }),
  });

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Аналитика"
        description="Как люди знакомятся с игрой и переходят к покупке. Ответы на карты, личные запросы и переживания не собираются."
        actions={
          <SegmentedControl
            value={period}
            onChange={setPeriod}
            data={PERIODS}
            aria-label="Период"
            data-testid="analytics-period"
          />
        }
      />
      <Stack gap="lg">
        {summary.isLoading ? (
          <LoadingState />
        ) : summary.isError || !summary.data ? (
          <ErrorState onRetry={() => void summary.refetch()} />
        ) : (
          <>
            <TrackingNote summary={summary.data} />
            <Funnel funnel={summary.data.funnel} />
            <Scenarios scenarios={summary.data.scenarios} />
            <Sources summary={summary.data} />
          </>
        )}
        <Events from={from} />
      </Stack>
    </>
  );
}

function TrackingNote({ summary }: { summary: AnalyticsSummary }) {
  return (
    <Text size="sm" c="var(--text-muted)" data-testid="analytics-since">
      {summary.trackingSince
        ? `События записываются с ${formatDate(summary.trackingSince, false)}. Раньше этой даты данных нет.`
        : 'Событий пока нет: журнал начинает заполняться с первой регистрации или демопартии.'}
    </Text>
  );
}

const FUNNEL_STEPS: { key: keyof AnalyticsFunnel; label: string; hint: string }[] = [
  { key: 'registered', label: 'Зарегистрировались', hint: 'за выбранный период' },
  { key: 'demoCreated', label: 'Создали демопартию', hint: 'любой сценарий' },
  { key: 'demoFinished', label: 'Дошли до итога демо', hint: 'обычный или досрочный' },
  {
    key: 'offerClicked',
    label: 'Нажали предложение полной игры',
    /* Шаг назван по действию: кнопку нажали, о покупке этим ещё ничего не сказано. */
    hint: 'кнопка в итоге демо; переход к своей игре не считается',
  },
  { key: 'paid', label: 'Оплатили', hint: 'подтверждённая оплата, без возвратов' },
];

/**
 * Воронка — одна серия величин, поэтому один цвет и подписи прямо у полос:
 * число и доля от зарегистрировавшихся. Полоса — подсказка глазу, число — главное.
 */
function Funnel({ funnel }: { funnel: AnalyticsFunnel }) {
  const base = funnel.registered;
  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>Воронка</Title>
          <Text size="sm" c="var(--text-muted)">
            Люди, зарегистрированные за период, и сколько из них сделали каждый следующий шаг — когда
            угодно до сегодняшнего дня.
          </Text>
        </div>
        <Stack gap="sm" data-testid="analytics-funnel">
          {FUNNEL_STEPS.map((step) => {
            const value = funnel[step.key];
            const width = base > 0 ? Math.max((value / base) * 100, value > 0 ? 1 : 0) : 0;
            return (
              <Tooltip
                key={step.key}
                label={`${step.label}: ${value} из ${base} (${share(value, base)})`}
                position="top-start"
                withinPortal
              >
                <Stack gap={4} data-testid={`funnel-${step.key}`}>
                  <Group justify="space-between" gap="xs" wrap="nowrap">
                    <Text size="sm">
                      {step.label}{' '}
                      <Text span size="xs" c="var(--text-subtle)">· {step.hint}</Text>
                    </Text>
                    <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
                      {value}
                      {step.key === 'registered' || base === 0 ? '' : (
                        <Text span size="xs" c="var(--text-muted)" fw={400}>
                          {' '}из {base} · {share(value, base)}
                        </Text>
                      )}
                    </Text>
                  </Group>
                  <div
                    aria-hidden
                    style={{ height: 10, borderRadius: 4, background: 'var(--border-subtle)', overflow: 'hidden' }}
                  >
                    <div style={{ width: `${width}%`, height: '100%', borderRadius: 4, background: 'var(--accent)' }} />
                  </div>
                </Stack>
              </Tooltip>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Строка сравнения сценариев. `of` — знаменатель доли: у шагов маршрута это
 * созданные партии, у ответов опроса — ответившие на этот вопрос. Знаменатель
 * виден на экране рядом с долей: при двух-трёх прохождениях «100%» сам по
 * себе не говорит ни о чём.
 */
type ScenarioRow = {
  key: string;
  label: string;
  value?: (stats: AnalyticsScenarioStats) => number;
  of?: (stats: AnalyticsScenarioStats) => number;
  strong?: boolean;
  /** Ответ на вопрос опроса — с отступом под самим вопросом. */
  indent?: boolean;
  /** Вместо чисел — пояснение на всю ширину (опрос, на который не отвечали). */
  note?: string;
};

const FEEDBACK_QUESTIONS = Object.keys(DEMO_FEEDBACK_QUESTIONS) as DemoFeedbackQuestion[];

const createdOf = (stats: AnalyticsScenarioStats): number => stats.created;

/** Сколько партий ответили на этот вопрос: на каждый вопрос партия отвечает один раз. */
const answeredOf = (question: DemoFeedbackQuestion) => (stats: AnalyticsScenarioStats): number =>
  Object.values(stats.feedback[question] as Record<string, number>).reduce((sum, count) => sum + count, 0);

/**
 * Опрос после итога. Ноль ответов — не то же, что ответ «Нет», поэтому пустой
 * опрос показан одной строкой, а не шестью нулями. Доли ответов считаются от
 * ответивших на этот вопрос, а не от всех партий: иначе одно прохождение
 * выглядит как «100% довольных».
 */
function feedbackRows(scenarios: AnalyticsScenarioStats[]): ScenarioRow[] {
  const answered = (question: DemoFeedbackQuestion): number =>
    scenarios.reduce((sum, stats) => sum + answeredOf(question)(stats), 0);
  if (FEEDBACK_QUESTIONS.every((question) => answered(question) === 0)) {
    return [{ key: 'feedback-empty', label: 'Опрос после итога', note: 'Ответов пока нет' }];
  }
  return [
    {
      key: 'feedback-parties',
      label: 'Ответили на опрос',
      value: (stats) => stats.feedbackParties,
      of: createdOf,
      strong: true,
    },
    ...FEEDBACK_QUESTIONS.flatMap((question): ScenarioRow[] => [
      {
        key: `feedback-${question}`,
        label: `«${DEMO_FEEDBACK_QUESTIONS[question].text}» — ответов`,
        value: answeredOf(question),
        of: createdOf,
      },
      ...Object.entries(DEMO_FEEDBACK_QUESTIONS[question].answers).map(([answer, label]): ScenarioRow => ({
        key: `feedback-${question}-${answer}`,
        label,
        indent: true,
        value: (stats) => (stats.feedback[question] as Record<string, number>)[answer] ?? 0,
        of: answeredOf(question),
      })),
    ]),
  ];
}

function scenarioRows(scenarios: AnalyticsScenarioStats[]): ScenarioRow[] {
  const sectors = scenarios[0]?.sectors ?? [];
  return [
    { key: 'people', label: 'Людей', value: (stats) => stats.people },
    { key: 'created', label: 'Создано демопартий', value: (stats) => stats.created, strong: true },
    { key: 'started', label: 'Партия началась', value: (stats) => stats.started, of: createdOf },
    {
      key: 'startTalent',
      label: 'Талант от рождения получен (Маяк)',
      value: (stats) => stats.startTalent,
      of: createdOf,
    },
    ...sectors.map((sector, index): ScenarioRow => ({
      key: `sector-${sector.sectorId}`,
      label: `${sector.title} пройден`,
      value: (stats) => stats.sectors[index]?.count ?? 0,
      of: createdOf,
    })),
    {
      key: 'finishedNormal',
      label: 'Итог: все прошли три сектора',
      value: (stats) => stats.finishedNormal,
      of: createdOf,
    },
    { key: 'finishedEarly', label: 'Итог: завершена досрочно', value: (stats) => stats.finishedEarly, of: createdOf },
    {
      key: 'ownGameClicks',
      label: 'Кнопка: переход к своей полной игре',
      value: (stats) => stats.ownGameClicks,
      of: createdOf,
    },
    { key: 'purchaseClicks', label: 'Кнопка: интерес к покупке', value: (stats) => stats.purchaseClicks, of: createdOf },
    ...feedbackRows(scenarios),
  ];
}

function Scenarios({ scenarios }: { scenarios: AnalyticsScenarioStats[] }) {
  const rows = scenarioRows(scenarios);
  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>«Для себя» и «как ведущий»</Title>
          <Text size="sm" c="var(--text-muted)">
            Демопартии, созданные за период, и что с ними было потом. Рядом с долей — от чего она
            считается: у шагов маршрута от созданных партий сценария, у ответов опроса от ответивших
            на этот вопрос. Перезагрузка страницы новой партией не считается.
          </Text>
        </div>
        <ScrollArea type="auto">
          <Table striped withTableBorder verticalSpacing={6} miw={520} data-testid="analytics-scenarios">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Шаг</Table.Th>
                {scenarios.map((stats) => (
                  <Table.Th key={stats.scenario} ta="right">{DEMO_SCENARIO_LABELS[stats.scenario]}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.key} data-testid={`scenario-${row.key}`}>
                  <Table.Td>
                    <Text size="sm" fw={row.strong ? 600 : undefined} pl={row.indent ? 'md' : undefined}>
                      {row.label}
                    </Text>
                  </Table.Td>
                  {row.note !== undefined ? (
                    <Table.Td colSpan={scenarios.length} ta="right">
                      <Text span size="sm" c="var(--text-muted)">{row.note}</Text>
                    </Table.Td>
                  ) : (
                    scenarios.map((stats) => {
                      const value = row.value?.(stats) ?? 0;
                      const base = row.of?.(stats);
                      return (
                        <Table.Td key={stats.scenario} ta="right" style={{ whiteSpace: 'nowrap' }}>
                          {base === 0 ? (
                            /* Измерять нечего: ни партий, ни ответов. Ноль здесь читался бы как оценка. */
                            <Text span size="sm" c="var(--text-muted)">—</Text>
                          ) : (
                            <>
                              <Text span size="sm" fw={row.strong ? 600 : undefined}>{value}</Text>
                              {base === undefined ? null : (
                                <Text span size="xs" c="var(--text-muted)"> из {base} · {share(value, base)}</Text>
                              )}
                            </>
                          )}
                        </Table.Td>
                      );
                    })
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>
    </Paper>
  );
}

function Sources({ summary }: { summary: AnalyticsSummary }) {
  if (summary.sources.length === 0) return null;
  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="md">
        <div>
          <Title order={4}>Источники регистраций</Title>
          <Text size="sm" c="var(--text-muted)">
            Метка <code>utm_source</code> из ссылки, по которой человек пришёл. Чтобы источник
            записался, добавляйте метки к ссылкам на платформу: <code>?utm_source=telegram</code>.
          </Text>
        </div>
        <ScrollArea type="auto">
          <Table striped withTableBorder verticalSpacing={6} miw={420} data-testid="analytics-sources">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Источник</Table.Th>
                <Table.Th ta="right">Регистраций</Table.Th>
                <Table.Th ta="right">Создали демо</Table.Th>
                <Table.Th ta="right">Оплатили</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {summary.sources.map((row) => (
                <Table.Tr key={row.source ?? '—'}>
                  <Table.Td>{row.source ?? <Text span size="sm" c="var(--text-muted)">не указан</Text>}</Table.Td>
                  <Table.Td ta="right">{row.registered}</Table.Td>
                  <Table.Td ta="right">{row.demoCreated}</Table.Td>
                  <Table.Td ta="right">{row.paid}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>
    </Paper>
  );
}

const TYPE_OPTIONS = [
  { value: 'all', label: 'Все события' },
  ...ANALYTICS_EVENT_TYPES.map((type) => ({ value: type, label: ANALYTICS_EVENT_LABELS[type] })),
];

function Events({ from }: { from: string | undefined }) {
  const [type, setType] = useState<string>('all');
  const filter: AnalyticsFilter = { from, type: type === 'all' ? undefined : (type as AnalyticsEventType) };
  const events = useQuery({
    queryKey: [...ADMIN_ANALYTICS_KEY, 'events', from ?? 'all', type],
    queryFn: () => fetchAnalyticsEvents(filter),
  });

  return (
    <Paper withBorder p="lg" radius="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
          <div>
            <Title order={4}>События</Title>
            <Text size="sm" c="var(--text-muted)">
              За выбранный период. В таблице для Excel вместо имён и почт — служебные номера.
            </Text>
          </div>
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label="Событие"
              data={TYPE_OPTIONS}
              value={type}
              onChange={(value) => setType(value ?? 'all')}
              allowDeselect={false}
              w={300}
              data-testid="analytics-type"
            />
            <Button
              component="a"
              href={analyticsExportUrl(filter)}
              download
              variant="light"
              leftSection={<IconDownload size={16} />}
              data-testid="analytics-export"
            >
              Скачать таблицу (CSV)
            </Button>
          </Group>
        </Group>

        {events.isLoading ? (
          <LoadingState />
        ) : events.isError || !events.data ? (
          <ErrorState onRetry={() => void events.refetch()} />
        ) : events.data.items.length === 0 ? (
          <EmptyState title="Событий нет" description="За этот период и с этим фильтром ничего не произошло." />
        ) : (
          <>
            {events.data.total > events.data.items.length ? (
              <Text size="sm" c="var(--text-muted)">
                Показаны последние {events.data.items.length} из {events.data.total}. Все — в таблице CSV.
              </Text>
            ) : null}
            <ScrollArea type="auto">
              <Table striped withTableBorder verticalSpacing={6} miw={760} data-testid="analytics-events">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Когда</Table.Th>
                    <Table.Th>Событие</Table.Th>
                    <Table.Th>Подробности</Table.Th>
                    <Table.Th>Сценарий</Table.Th>
                    <Table.Th>Кто</Table.Th>
                    <Table.Th>Источник</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {events.data.items.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatDate(item.occurredAt)}</Table.Td>
                      <Table.Td>{ANALYTICS_EVENT_LABELS[item.type]}</Table.Td>
                      <Table.Td>{analyticsDetailLabel(item.type, item.detail)}</Table.Td>
                      <Table.Td>{item.scenario ? DEMO_SCENARIO_LABELS[item.scenario] : ''}</Table.Td>
                      <Table.Td>
                        {item.userId === null ? (
                          <Text span size="sm" c="var(--text-muted)">аккаунт удалён</Text>
                        ) : (
                          <Stack gap={0}>
                            <Text size="sm">{item.userDisplayName}</Text>
                            <Text size="xs" c="var(--text-muted)">{item.userEmail}</Text>
                          </Stack>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {[item.utmSource, item.utmMedium, item.utmCampaign].filter(Boolean).join(' / ')}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </>
        )}
      </Stack>
    </Paper>
  );
}
